import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Form, Button, message } from 'antd';
import api from '../services/api';
import type { LocationPayload } from '../types/location';
import { MerchantOnboardingFormContent } from '../components/merchants/MerchantOnboardingFormContent';

export default function AddMerchant() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [location, setLocation] = React.useState<LocationPayload | null>(null);
  /** Tránh POST /merchants hai lần (double-submit) — lần 1 thành công, lần 2 trả PHONE_EXISTS gây hiểu nhầm */
  const submitLockRef = React.useRef(false);

  const onFinish = async (values: Record<string, unknown>) => {
    const name = String(values.name || '').trim();
    const phone = values.phone ? String(values.phone).trim() : '';
    const password = String(values.password || '');
    const restaurantName = String(values.restaurantName || '').trim();
    const restaurantAddress =
      location?.address ?? String(values.restaurantAddress || '').trim();

    const lat = location?.lat != null ? Number(location.lat) : undefined;
    const lng = location?.lng != null ? Number(location.lng) : undefined;

    if (!name || !phone || !password || !restaurantName) {
      message.error('Vui lòng điền đầy đủ các trường bắt buộc');
      return;
    }
    if (lat == null || lng == null) {
      message.error('Vui lòng tìm kiếm địa chỉ hoặc chọn vị trí trên bản đồ');
      return;
    }
    if (password.length < 8) {
      message.error(
        'Mật khẩu phải có ít nhất 8 ký tự (merchant dùng mật khẩu này để đăng nhập app)'
      );
      return;
    }

    if (submitLockRef.current) {
      return;
    }
    submitLockRef.current = true;

    try {
      setLoading(true);
      form.setFields([{ name: 'phone', errors: [] }]);
      const res = await api.post('/merchants', {
        name,
        phone,
        address: values.address ? String(values.address).trim() : undefined,
        password,
        restaurantName,
        restaurantAddress: restaurantAddress || undefined,
        latitude: lat,
        longitude: lng,
      });
      if (res.data?.success) {
        const generatedEmail = res.data?.data?.email as string | undefined;
        message.success(
          generatedEmail
            ? `Tạo quán thành công. Email: ${generatedEmail}`
            : 'Tạo quán thành công'
        );
        navigate('/merchants?refresh=1');
      } else {
        message.error(res.data?.message || 'Tạo quán thất bại');
      }
    } catch (err: unknown) {
      const ax = err as {
        response?: {
          data?: {
            code?: string;
            message?: string;
          };
          status?: number;
        };
      };
      const code = ax.response?.data?.code;
      const msg = ax.response?.data?.message;
      if (code === 'PHONE_EXISTS') {
        message.error(msg || 'Số điện thoại đã được dùng.');
        form.setFields([
          { name: 'phone', errors: [msg || 'Số điện thoại đã tồn tại'] },
        ]);
      } else if (code === 'EMAIL_EXISTS') {
        message.error(msg || 'Email đã tồn tại.');
      } else if (code === 'WEAK_PASSWORD') {
        message.error(msg || 'Mật khẩu phải có ít nhất 8 ký tự');
        form.setFields([
          { name: 'password', errors: [msg || 'Ít nhất 8 ký tự'] },
        ]);
      } else {
        message.error(msg || 'Tạo quán thất bại');
      }
    } finally {
      setLoading(false);
      submitLockRef.current = false;
    }
  };

  return (
    <div className="merchant-detail-page">
      <header className="merchant-detail-header">
        <h1 className="merchant-detail-title">Add Merchant</h1>
        <nav className="merchant-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <Link to="/merchants">Merchants</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <span className="merchant-detail-breadcrumb-current">
            Add Merchant
          </span>
        </nav>
      </header>

      <Card style={{ maxWidth: 640 }}>
        <p style={{ marginBottom: 16, color: 'rgba(0,0,0,0.65)' }}>
          Chỉ tạo tài khoản đăng nhập và thông tin quán cơ bản. Merchant sẽ tự
          đăng nhập app và hoàn thành KYC như đăng ký thông thường.
        </p>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ maxWidth: 560 }}
        >
          <MerchantOnboardingFormContent
            form={form}
            location={location}
            onLocationChange={setLocation}
            onAddressTextChange={addr => {
              if (location) {
                setLocation(p => (p ? { ...p, address: addr } : null));
              }
            }}
            showPromoCreditsField={false}
            showEmailField={false}
          />
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Create Merchant
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => navigate('/merchants')}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
